import { useTheme } from '@/context/ThemeContext';
import { competitorListings } from '@/mocks/fizboData';

interface RakipKarsilastirmaProps {
  myPrice: number;
  mySqm: number;
  myLocation: string;
}

export default function RakipKarsilastirma({ myPrice, mySqm, myLocation }: RakipKarsilastirmaProps) {
  const { isDark } = useTheme();

  const myPricePerSqm = Math.round(myPrice / mySqm);
  const avgCompetitorPrice = Math.round(
    competitorListings.reduce((sum, c) => sum + c.price, 0) / competitorListings.length
  );
  const avgPricePerSqm = Math.round(
    competitorListings.reduce((sum, c) => sum + c.pricePerSqm, 0) / competitorListings.length
  );
  const avgDaysOnMarket = Math.round(
    competitorListings.reduce((sum, c) => sum + c.daysOnMarket, 0) / competitorListings.length
  );

  const priceDiff = ((myPrice - avgCompetitorPrice) / avgCompetitorPrice) * 100;
  const priceDiffLabel = priceDiff > 0 ? `+%${priceDiff.toFixed(1)} piyasa üstü` : `%${Math.abs(priceDiff).toFixed(1)} piyasa altı`;
  const priceDiffColor = priceDiff > 5 ? 'text-red-500' : priceDiff < -5 ? 'text-green-500' : 'text-yellow-500';

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-2">
        {[
          { label: 'Ort. Rakip Fiyatı', value: `${(avgCompetitorPrice / 1000000).toFixed(2)}M ₺`, icon: 'ri-price-tag-3-line' },
          { label: 'Ort. m² Fiyatı', value: `${avgPricePerSqm.toLocaleString('tr-TR')} ₺`, icon: 'ri-ruler-line' },
          { label: 'Ort. Satış Süresi', value: `${avgDaysOnMarket} gün`, icon: 'ri-time-line' },
        ].map((stat) => (
          <div key={stat.label} className={`rounded-xl p-3 text-center ${isDark ? 'bg-dark-bg' : 'bg-light-bg'}`}>
            <div className="w-6 h-6 flex items-center justify-center mx-auto mb-1">
              <i className={`${stat.icon} text-sm text-primary-500`} />
            </div>
            <p className={`text-sm font-bold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>{stat.value}</p>
            <p className={`text-xs mt-0.5 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>{stat.label}</p>
          </div>
        ))}
      </div>

      {/* My Listing vs Market */}
      <div className={`rounded-xl p-4 ${isDark ? 'bg-dark-bg' : 'bg-light-bg'}`}>
        <p className={`text-xs font-semibold mb-3 ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
          İlanınız vs Piyasa
        </p>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>Fiyat Konumu</span>
            <span className={`text-xs font-semibold ${priceDiffColor}`}>{priceDiffLabel}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>m² Fiyatınız</span>
            <span className={`text-xs font-semibold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
              {myPricePerSqm.toLocaleString('tr-TR')} ₺/m²
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>Piyasa m² Ortalaması</span>
            <span className={`text-xs font-semibold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
              {avgPricePerSqm.toLocaleString('tr-TR')} ₺/m²
            </span>
          </div>
        </div>
      </div>

      {/* Competitor Table */}
      <div>
        <p className={`text-xs font-semibold mb-2 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
          Bölgedeki Rakip İlanlar ({myLocation.split(',')[0]})
        </p>
        <div className={`rounded-xl overflow-hidden border ${isDark ? 'border-dark-border' : 'border-light-border'}`}>
          <table className="w-full text-xs">
            <thead>
              <tr className={isDark ? 'bg-dark-bg text-dark-muted' : 'bg-light-bg text-light-muted'}>
                <th className="px-3 py-2 text-left font-medium">Firma</th>
                <th className="px-3 py-2 text-right font-medium">Fiyat</th>
                <th className="px-3 py-2 text-right font-medium">m²</th>
                <th className="px-3 py-2 text-right font-medium">Gün</th>
                <th className="px-3 py-2 text-center font-medium">Durum</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-dark-border' : 'divide-light-border'}`}>
              {competitorListings.map((c) => (
                <tr key={c.id} className={isDark ? 'text-dark-text' : 'text-light-text'}>
                  <td className="px-3 py-2.5 font-medium">{c.agency}</td>
                  <td className="px-3 py-2.5 text-right">{(c.price / 1000000).toFixed(2)}M ₺</td>
                  <td className="px-3 py-2.5 text-right">{c.sqm} m²</td>
                  <td className={`px-3 py-2.5 text-right ${c.daysOnMarket > 45 ? 'text-red-500' : c.daysOnMarket > 20 ? 'text-yellow-500' : 'text-green-500'}`}>
                    {c.daysOnMarket}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      c.status === 'Aktif'
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                    }`}>
                      {c.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Price Bar Chart */}
      <div className={`rounded-xl p-4 ${isDark ? 'bg-dark-bg' : 'bg-light-bg'}`}>
        <p className={`text-xs font-semibold mb-3 ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
          Fiyat Karşılaştırması
        </p>
        <div className="space-y-2">
          {[
            { label: 'İlanınız', price: myPrice, isMe: true },
            ...competitorListings.slice(0, 4).map((c) => ({ label: c.agency, price: c.price, isMe: false })),
          ].map((item) => {
            const maxPrice = Math.max(myPrice, ...competitorListings.map((c) => c.price));
            const pct = (item.price / maxPrice) * 100;
            return (
              <div key={item.label} className="flex items-center gap-2">
                <span className={`text-xs w-24 truncate flex-shrink-0 ${item.isMe ? 'font-semibold text-primary-500' : isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                  {item.label}
                </span>
                <div className={`flex-1 h-2 rounded-full ${isDark ? 'bg-dark-border' : 'bg-light-border'}`}>
                  <div
                    className={`h-full rounded-full transition-all duration-700 ${item.isMe ? 'bg-primary-500' : 'bg-gray-400 dark:bg-gray-600'}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className={`text-xs w-16 text-right flex-shrink-0 ${item.isMe ? 'font-semibold text-primary-500' : isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                  {(item.price / 1000000).toFixed(2)}M
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
